export const ERROR_MESSAGES = {
  network: "No internet connection. Please check your network and try again.",
  timeout: "Request timed out. Please try again.",
  server: "Something went wrong. Please try again later.",
  unknown: "An unexpected error occurred. Please try again.",
} as const;

/* ----------------------------------
 * HTTP status code -> user message map
 * Reused by both statusInfo and getApiErrorMessage
 * ----------------------------------*/
export const statusInfo: Record<
  number | "default",
  { title: string; message: string; type: "error" | "success" }
> = {
  400: {
    title: "Bad Request",
    message: "The request could not be understood.",
    type: "error",
  },
  401: {
    title: "Unauthorized",
    message: "Please login again.",
    type: "error",
  },
  403: {
    title: "Access Denied",
    message: "You don't have permission.",
    type: "error",
  },
  404: {
    title: "Page Not Found",
    message: "The page you are looking for does not exist.",
    type: "error",
  },
  408: {
    title: "Request Timeout",
    message: "Request timed out. Please try again.",
    type: "error",
  },
  500: {
    title: "Server Error",
    message: "Something went wrong on our side.",
    type: "error",
  },
  default: {
    title: "Oops!",
    message: "Something went wrong.",
    type: "error",
  },
};

/* ----------------------------------
 * Resolve message from HTTP status code
 * Uses statusInfo map - falls back to server/timeout/unknown
 * ----------------------------------*/
const getMessageFromStatus = (status: number): string => {
  if (status === 408) return ERROR_MESSAGES.timeout;
  if (status >= 500) return ERROR_MESSAGES.server;

  // For 400, 401, 403, 404 — use statusInfo message directly
  const info = statusInfo[status as keyof typeof statusInfo];
  if (info) return info.message;

  return ERROR_MESSAGES.unknown;
};

/* ----------------------------------
 * Maps a caught error to a user-friendly message string.
 * Priority order:
 *   1. Offline / network check
 *   2. Fetch API TypeError
 *   3. AbortController timeout
 *   4. Backend errorCode field  <- NEW
 *   5. HTTP status code
 *   6. Unknown fallback
 * ----------------------------------*/
export const getApiErrorMessage = (error: unknown): string => {
  // 1️. Network / offline check
  if (!navigator.onLine) {
    return ERROR_MESSAGES.network;
  }

  // 2️. Fetch API network failure
  if (error instanceof TypeError && error.message === "Failed to fetch") {
    return ERROR_MESSAGES.network;
  }

  // 3️. Timeout - AbortController / DOMException
  if (error instanceof DOMException && error.name === "AbortError") {
    return ERROR_MESSAGES.timeout;
  }

  if (typeof error === "object" && error !== null) {
    const err = error as {
      errorCode?: number | string;
      status?: number;
      response?: {
        status?: number;
        data?: { errorCode?: number | string };
      };
    };

    // 4️. Backend errorCode field - checked BEFORE HTTP status
    //    Covers both top-level errorCode and nested response.data.errorCode
    const errorCode = err.errorCode ?? err.response?.data?.errorCode;

    if (errorCode !== undefined) {
      const code = Number(errorCode);
      const info = statusInfo[code as keyof typeof statusInfo];
      // Return the mapped message if known, otherwise fall through to status check
      if (info) return info.message;
    }

    // 5️. HTTP status-based detection (Axios or fetch wrappers)
    const status = err.status ?? err.response?.status;

    if (status !== undefined) {
      return getMessageFromStatus(status);
    }
  }

  // 6️. Fallback — unknown / unexpected
  return ERROR_MESSAGES.unknown;
};

